import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

interface PortalDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    triggerRef: React.RefObject<HTMLElement> | { current: HTMLElement | null };
    children: React.ReactNode;
    className?: string;
    align?: 'left' | 'right';
}

export const PortalDropdown: React.FC<PortalDropdownProps> = ({
    isOpen,
    onClose,
    triggerRef,
    children,
    className = '',
    align = 'right'
}) => {
    const [position, setPosition] = useState<{ top: number; left: number | 'auto'; right: number | 'auto' } | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Update position when opened
    useLayoutEffect(() => {
        if (isOpen && triggerRef.current) {
            const updatePosition = () => {
                if (!triggerRef.current) return;
                const rect = triggerRef.current.getBoundingClientRect();

                // Fixed positioning relative to viewport
                const top = rect.bottom + 4;
                let left: number | 'auto' = 'auto';
                let right: number | 'auto' = 'auto';

                if (align === 'right') {
                    right = window.innerWidth - rect.right;
                } else {
                    left = rect.left;
                }

                // Vertical boundary check: if close to bottom, show above?
                // For now, keep simple.

                setPosition({ top, left, right });
            };

            updatePosition();

            // Update on scroll/resize
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        } else {
            setPosition(null);
        }
    }, [isOpen, align, triggerRef]);

    useEffect(() => {
        if (!isOpen) return;

        const handleMouseDown = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(e.target as Node)
            ) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [isOpen, onClose, triggerRef]);

    if (!isOpen || !position) return null;

    const originClass = align === 'right' ? 'origin-top-right' : 'origin-top-left';

    return createPortal(
        <div
            ref={dropdownRef}
            className={`fixed z-[9999] bg-white border border-neutral-200 rounded-md shadow-lg py-1 animate-in fade-in zoom-in-95 duration-100 ${originClass} ${className}`}
            style={{
                top: position.top,
                left: position.left === 'auto' ? 'auto' : position.left,
                right: position.right === 'auto' ? 'auto' : position.right,
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {children}
        </div>,
        document.body
    );
};
